import { columnField, type AntdColumn } from "./columnAdapter";

// Pure export serialization for DataTable. Community DataGrid ships CSV export
// via its toolbar, but this gives programmatic export (a button anywhere) and a
// single matrix layer that a real .xlsx exporter (exceljs) can sit on later —
// not added now to avoid a speculative dependency; CSV opens in Excel fine.

type Cell = string | number | boolean;

export const tableToMatrix = <T,>(columns: AntdColumn<T>[], rows: T[]): Cell[][] => {
  const header: Cell[] = columns.map((c) =>
    typeof c.title === "string" || typeof c.title === "number" ? c.title : String(c.title ?? ""),
  );
  const body: Cell[][] = rows.map((row) =>
    columns.map((c, i) => {
      const v = (row as Record<string, unknown>)[columnField(c, i)];
      if (v === null || v === undefined) return "";
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
      return String(v);
    }),
  );
  return [header, ...body];
};

const escapeCsv = (cell: Cell): string => {
  const s = String(cell);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const matrixToCsv = (matrix: Cell[][]): string =>
  matrix.map((row) => row.map(escapeCsv).join(",")).join("\r\n");

export const tableToCsv = <T,>(columns: AntdColumn<T>[], rows: T[]): string =>
  matrixToCsv(tableToMatrix(columns, rows));

// Browser download trigger (side-effecting; covered by the app/Cypress, not unit tests).
export const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
